import { contactBlock } from "./legal";

export const refundPolicy = {
  title: "Refund & Cancellation Policy",
  updated: "14 September 2026",
  heroCopy:
    "Learn how cancellations, refunds, project payments, third-party costs, and digital service charges are handled by Bytevora.",
  toc: [
    { label: "Scope", id: "scope-of-this-policy" },
    { label: "Advance Payments", id: "advance-payments" },
    { label: "Cancellation", id: "cancellation-before-work-begins" },
    { label: "Milestones", id: "milestone-based-projects" },
    { label: "Third-Party Costs", id: "third-party-costs" },
    { label: "Subscriptions", id: "subscription-or-recurring-services" },
    { label: "Digital Deliverables", id: "digital-deliverables" },
    { label: "Refund Requests", id: "refund-request-process" },
    { label: "Processing", id: "refund-processing-time" },
    { label: "Non-Refundable Items", id: "non-refundable-items" },
    { label: "Contact", id: "contact-us" },
  ],
  intro: [
    "This Refund & Cancellation Policy explains the circumstances in which payments made to Bytevora IT Solutions Private Limited (“Bytevora”, “we”, “us”, or “our”) may be refundable, non-refundable, cancelled, adjusted, or credited.",
    "Because Bytevora primarily provides customized technology and digital services, refund eligibility may depend on the type of service, project stage, work already performed, resources allocated, third-party costs incurred, and the terms of the applicable quotation, proposal, invoice, or agreement.",
    "By making a payment to Bytevora, you acknowledge and agree to this Policy together with our applicable Terms & Conditions and project-specific agreement.",
  ],
  sections: [
    {
      id: "scope-of-this-policy",
      title: "Scope of This Policy",
      blocks: [
        { type: "p", text: "This Policy applies to payments made for services including, but not limited to:" },
        {
          type: "list",
          items: [
            "Software development", "Web development", "Mobile application development", "SaaS and product development", "AI and automation services", "Cloud and infrastructure services", "Digital transformation", "Technology consulting", "Technical support", "Maintenance", "Design and digital deliverables", "Other technology-related services provided by Bytevora",
          ],
        },
        { type: "p", text: "Where a separate written agreement contains specific refund or cancellation terms, those project-specific terms will generally take precedence." },
      ],
    },
    {
      id: "nature-of-bytevora-services",
      title: "Nature of Bytevora Services",
      blocks: [
        { type: "p", text: "Many Bytevora services are customized for individual clients." },
        { type: "p", text: "Once a project begins, Bytevora may allocate development time, engineering resources, design resources, infrastructure, software licences, third-party services, cloud resources, project management time, technical planning, and research and implementation effort." },
        { type: "p", text: "For this reason, payments may become partially or fully non-refundable once work has started or costs have been incurred." },
      ],
    },
    {
      id: "advance-payments",
      title: "Advance Payments",
      blocks: [
        { type: "p", text: "Bytevora may require an advance payment before beginning work." },
        { type: "p", segments: [{ t: "Unless otherwise stated in writing, " }, { t: "advance payments are generally used to reserve resources, begin planning, and commence project work.", strong: true }] },
        { type: "p", text: "If the client cancels the project before any work has started and before any non-refundable costs have been incurred, Bytevora may consider a refund of the advance payment, subject to reasonable administrative or payment-processing deductions." },
        { type: "p", text: "Once work has commenced, refund eligibility will depend on the amount of work already completed and costs already incurred." },
      ],
    },
    {
      id: "cancellation-before-work-begins",
      title: "Cancellation Before Work Begins",
      blocks: [
        { type: "p", text: "If a client requests cancellation before project work has started, Bytevora may refund eligible amounts after deducting, where applicable:" },
        {
          type: "list",
          items: ["Payment gateway charges", "Bank charges", "Currency conversion costs", "Administrative charges", "Non-refundable third-party costs", "Any work already performed during discovery or planning"],
        },
        { type: "p", text: "A refund is not automatic and will be reviewed based on the circumstances." },
      ],
    },
    {
      id: "cancellation-after-work-has-started",
      title: "Cancellation After Work Has Started",
      blocks: [
        { type: "p", text: "Once work has started, payments corresponding to completed work, allocated resources, reserved time, or incurred costs may be non-refundable." },
        { type: "p", text: "If cancellation occurs after commencement, Bytevora may calculate the refundable amount based on work completed, time spent, milestones reached, resources allocated, third-party costs, software or infrastructure purchases, non-cancellable commitments, and deliverables already provided." },
        { type: "p", text: "If the value of work already completed equals or exceeds the amount paid, no refund may be due." },
      ],
    },
    {
      id: "milestone-based-projects",
      title: "Milestone-Based Projects",
      blocks: [
        { type: "p", text: "Where a project uses milestone payments:" },
        {
          type: "list",
          items: [
            "Payments for completed or substantially completed milestones are generally non-refundable.",
            "Work may begin on the next milestone only after the required payment is received.",
            "Cancellation does not automatically create a refund entitlement for earlier completed milestones.",
            "Any refund relating to an unfinished milestone will depend on the work already completed and expenses incurred.",
          ],
        },
        { type: "p", text: "Milestone definitions may be stated in the applicable proposal, quotation, or contract." },
      ],
    },
    {
      id: "custom-development-services",
      title: "Custom Development Services",
      blocks: [
        { type: "p", text: "Payments for custom development may become non-refundable once development has begun." },
        { type: "p", text: "This may include work such as custom software, websites, web applications, mobile applications, APIs, SaaS platforms, integrations, automation systems, custom backend systems, cloud implementations, and technical architecture." },
        { type: "p", text: "Because such work is created specifically for the client, it may have limited or no resale value." },
      ],
    },
    {
      id: "discovery-consultation-and-planning",
      title: "Discovery, Consultation, and Planning",
      blocks: [
        { type: "p", text: "Payments for discovery sessions, technical consulting, strategy sessions, architecture planning, audits, assessments, research, and workshops are generally non-refundable once the service has been delivered or the allocated consultation time has been used." },
        { type: "p", text: "If a scheduled consultation is cancelled sufficiently in advance, rescheduling may be offered at Bytevora’s discretion." },
      ],
    },
    {
      id: "third-party-costs",
      title: "Third-Party Costs",
      blocks: [
        { type: "p", text: "Third-party expenses are generally non-refundable once incurred." },
        {
          type: "list",
          items: ["Domain registration", "Hosting fees", "Cloud charges", "Software licences", "API usage", "SaaS subscriptions", "Premium plugins", "App-store fees", "Third-party tools", "Payment gateway fees", "SMS or communication credits", "External service charges"],
        },
        { type: "p", text: "Refund availability for third-party services depends on the third party’s own policies." },
      ],
    },
    {
      id: "subscription-or-recurring-services",
      title: "Subscription or Recurring Services",
      blocks: [
        { type: "p", text: "If Bytevora offers recurring services, subscriptions, hosting, maintenance, support, retainers, or SaaS services, cancellation terms may be specified separately." },
        { type: "p", text: "Unless otherwise stated:" },
        {
          type: "list",
          items: [
            "Cancellation normally applies to future billing periods",
            "Fees already charged for the current billing period may be non-refundable",
            "Partial-month or prorated refunds are not guaranteed",
            "Outstanding charges remain payable",
          ],
        },
        { type: "p", text: "Any specific subscription terms will be communicated before purchase." },
      ],
    },
    {
      id: "hosting-cloud-and-infrastructure-services",
      title: "Hosting, Cloud, and Infrastructure Services",
      blocks: [
        { type: "p", text: "Payments for hosting, cloud resources, servers, infrastructure, or third-party environments may be non-refundable where the resource has already been provisioned or purchased." },
        { type: "p", text: "Where Bytevora pays an external provider on behalf of the client, the refundability of such charges will depend on the relevant provider’s policy." },
      ],
    },
    {
      id: "digital-deliverables",
      title: "Digital Deliverables",
      blocks: [
        { type: "p", text: "Once a digital deliverable has been completed and delivered, payments for that deliverable are generally non-refundable." },
        { type: "p", text: "Digital deliverables may include source code, design files, documentation, reports, credentials, configuration files, deployments, software packages, technical documents, and other electronically delivered materials." },
        { type: "p", text: "This does not affect any correction or warranty obligations expressly stated in the applicable project agreement." },
      ],
    },
    {
      id: "change-of-mind",
      title: "Change of Mind",
      blocks: [
        { type: "p", text: "Refunds are generally not provided solely because a client changes their mind, changes business direction, decides not to use the completed work, no longer requires the project, changes management, changes branding, or changes internal priorities after Bytevora has already performed work or incurred costs." },
      ],
    },
    {
      id: "delays-caused-by-the-client",
      title: "Delays Caused by the Client",
      blocks: [
        { type: "p", text: "Refunds are generally not available where delays arise because the client fails to provide required content, feedback, approvals, credentials, access, technical information, decisions, or required legal or business information." },
        { type: "p", text: "Bytevora may pause the project until the required information is received." },
        { type: "p", text: "Long periods of inactivity may be handled according to the relevant agreement." },
      ],
    },
    {
      id: "project-inactivity-or-abandonment",
      title: "Project Inactivity or Abandonment",
      blocks: [
        { type: "p", text: "If a project remains inactive for an extended period because the client does not provide required feedback, information, approvals, or communication, Bytevora may classify the project as paused or abandoned." },
        { type: "p", text: "In such cases, previous payments may remain non-refundable, reactivation may require a new schedule, additional fees may apply, and previously reserved resources may no longer be available." },
        { type: "p", text: "Specific inactivity periods may be defined in project agreements." },
      ],
    },
    {
      id: "cancellation-by-bytevora",
      title: "Cancellation by Bytevora",
      blocks: [
        { type: "p", text: "Bytevora may cancel or terminate a project where reasonably necessary due to non-payment, fraud, illegal activity, abusive conduct, security risks, material breach, repeated failure to cooperate, requests that violate law or third-party rights, or circumstances that make continued delivery impractical." },
        { type: "p", text: "If Bytevora cancels a project for reasons not caused by the client, we may refund the portion of prepaid fees relating to work not yet performed, after accounting for completed work and non-refundable expenses." },
      ],
    },
    {
      id: "quality-concerns-and-defects",
      title: "Quality Concerns and Defects",
      blocks: [
        { type: "p", text: "If a client believes that a deliverable does not match the agreed requirements, they should contact Bytevora promptly with reasonable details." },
        { type: "p", text: "Where appropriate, Bytevora may first attempt to investigate the issue, correct defects, fix errors, complete omitted agreed functionality, or provide a reasonable remedy." },
        { type: "p", text: "A refund is not necessarily the first remedy where the issue can reasonably be corrected." },
      ],
    },
    {
      id: "refunds-for-duplicate-payments",
      title: "Refunds for Duplicate Payments",
      blocks: [
        { type: "p", text: "If a customer is charged twice for the same invoice or transaction due to an accidental duplicate payment, Bytevora will review the transaction and refund any confirmed duplicate amount, subject to payment gateway or banking deductions where applicable." },
      ],
    },
    {
      id: "failed-transactions",
      title: "Failed Transactions",
      blocks: [
        { type: "p", text: "If a payment appears failed but funds are debited from the customer’s account, the customer should first allow the payment provider or bank sufficient time to complete its automatic reconciliation process." },
        { type: "p", text: "Where Bytevora does not receive the payment, the refund or reversal is generally handled by the payment gateway, bank, or financial institution." },
        { type: "p", text: "Customers may contact Bytevora with the transaction reference if assistance is required." },
      ],
    },
    {
      id: "chargebacks-and-payment-disputes",
      title: "Chargebacks and Payment Disputes",
      blocks: [
        { type: "p", text: "Before initiating a chargeback or payment dispute, customers are encouraged to contact Bytevora so that the issue can be reviewed." },
        { type: "p", text: "Improper or fraudulent chargebacks may result in suspension of services, suspension of project work, withholding of pending deliverables, or termination of the commercial relationship." },
        { type: "p", text: "Bytevora reserves the right to submit relevant transaction, delivery, communication, and project records to the payment provider or financial institution when responding to a dispute." },
      ],
    },
    {
      id: "refund-request-process",
      title: "Refund Request Process",
      blocks: [
        { type: "p", text: "To request a refund, contact Bytevora with:" },
        {
          type: "list",
          items: ["Full name", "Company name, where applicable", "Invoice or order number", "Transaction reference", "Payment date", "Amount paid", "Service or project name", "Reason for requesting a refund"],
        },
        {
          type: "p",
          segments: [
            { t: "Send requests to " },
            { link: "hello@bytevora.com", to: "mailto:hello@bytevora.com" },
            { t: ". Bytevora may request additional information where necessary to review the request." },
          ],
        },
      ],
    },
    {
      id: "refund-review",
      title: "Refund Review",
      blocks: [
        { type: "p", text: "Refund requests are reviewed individually." },
        { type: "p", text: "We may consider project status, work completed, time spent, deliverables provided, milestones completed, costs incurred, third-party expenses, contractual commitments, client responsibilities, and the nature of the cancellation." },
        { type: "p", text: "Approval or rejection of a refund request will depend on the circumstances and applicable agreement." },
      ],
    },
    {
      id: "refund-processing-time",
      title: "Refund Processing Time",
      blocks: [
        { type: "p", text: "If a refund is approved, Bytevora will initiate it through the appropriate payment method or another mutually agreed method." },
        { type: "p", text: "The time required for the amount to appear in the customer’s account may depend on the payment gateway, bank, card issuer, payment method, country, currency, and financial institution processing times." },
        { type: "p", text: "Bytevora does not control external banking or gateway settlement times." },
      ],
    },
    {
      id: "refund-method",
      title: "Refund Method",
      blocks: [
        { type: "p", text: "Where reasonably possible, approved refunds will be sent to the original payment method." },
        { type: "p", text: "In some cases, an alternative method may be used where the original method cannot accept the refund, the payment gateway requires another process, or the parties mutually agree to another method." },
        { type: "p", text: "Identity or account verification may be required for security purposes." },
      ],
    },
    {
      id: "currency-conversion-and-bank-charges",
      title: "Currency Conversion and Bank Charges",
      blocks: [
        { type: "p", text: "Where payments involve foreign currencies, the amount ultimately received after a refund may vary because of exchange-rate movements, international bank fees, card network charges, or payment processor fees." },
        { type: "p", text: "Bytevora is not responsible for differences caused solely by external currency conversion or banking charges." },
      ],
    },
    {
      id: "non-refundable-items",
      title: "Non-Refundable Items",
      blocks: [
        { type: "p", text: "Unless otherwise agreed in writing, the following may be non-refundable once provided, allocated, purchased, or incurred:" },
        {
          type: "list",
          items: ["Completed work", "Accepted milestones", "Consultation time", "Discovery work", "Technical planning", "Custom development", "Delivered digital files", "Source code already transferred", "Third-party licences", "Domain fees", "Hosting or cloud costs", "API charges", "SaaS subscriptions", "Payment processing charges", "Reserved development time", "External vendor costs"],
        },
      ],
    },
    {
      id: "credits-instead-of-refunds",
      title: "Credits Instead of Refunds",
      blocks: [
        { type: "p", text: "In some circumstances, Bytevora may offer a service credit instead of a cash refund." },
        { type: "p", text: "A credit may be used toward future development, support, maintenance, consulting, or additional project work." },
        { type: "p", text: "Credits are offered at Bytevora’s discretion and may be subject to an expiry period communicated at the time." },
      ],
    },
    {
      id: "no-automatic-refund-because-of-third-party-failure",
      title: "No Automatic Refund Because of Third-Party Failure",
      blocks: [
        { type: "p", text: "Bytevora is not automatically required to refund service fees solely because a third-party provider experiences downtime, API failure, service discontinuation, pricing changes, policy changes, or account restrictions." },
        { type: "p", text: "Where appropriate, Bytevora may assist the client with alternatives or mitigation." },
      ],
    },
    {
      id: "relationship-with-other-agreements",
      title: "Relationship With Other Agreements",
      blocks: [
        { type: "p", text: "This Policy should be read together with the Terms & Conditions, Shipping & Delivery Policy, Privacy Policy, Cookie Policy, Disclaimer, and any applicable quotation, proposal, statement of work, service agreement, or contract." },
        { type: "p", text: "Where there is a conflict between this general website Policy and a specific signed agreement, the specific agreement will generally govern the relevant project." },
      ],
    },
    {
      id: "changes-to-this-policy",
      title: "Changes to This Policy",
      blocks: [
        { type: "p", text: "Bytevora may update this Refund & Cancellation Policy from time to time to reflect changes in services, billing models, payment methods, payment gateways, business processes, or legal requirements." },
        { type: "p", text: "The latest version will be published on this page with an updated revision date." },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        { type: "p", text: "For questions, cancellations, or refund requests, please contact:" },
        contactBlock,
      ],
    },
  ],
};
